/**
 * Patient Medication Tab Component
 * 
 * Redesigned medication management interface integrated directly into patient dashlets.
 * Replaces the separate MedPass workflow with context-aware medication ordering.
 * 
 * Key Features:
 * - Patient-context aware (no manual patient selection)
 * - Integrated medication history and current prescriptions
 * - Streamlined medication ordering workflow
 * - Real-time safety validation
 * - Comprehensive audit trail
 */

import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Button,
    Alert,
    Tabs,
    Table,
    Badge,
    Space,
    Modal,
    message,
    Tag,
    List,
    Statistic,
    Typography
} from 'antd';
import {
    PlusOutlined,
    MedicineBoxOutlined,
    SafetyCertificateOutlined,
    HistoryOutlined,
    ExclamationCircleOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    UserOutlined,
    FileProtectOutlined,
    PlayCircleOutlined,
    PauseCircleOutlined,
    FormOutlined
} from '@ant-design/icons';
import moment from 'moment';

import PatientContextMedPassWorkflow from './PatientContextMedPassWorkflow';
import { AdministrationSessionManager } from './AdministrationSession';
import medicationService from '../../services/medicationService';
import medpassService from '../../services/medpassService';
import SmartMedicationDashboard from './SmartMedicationDashboard';

// const { TabPane } = Tabs; // Deprecated - using items prop instead
const { Text } = Typography;

const PatientMedicationTab = ({ patient, onMedicationOrdered }) => {
    // State management
    const [activeTab, setActiveTab] = useState('schedule');
    const [currentMedications, setCurrentMedications] = useState([]);
    const [scheduledMedications, setScheduledMedications] = useState([]);
    const [activeWorkflows, setActiveWorkflows] = useState([]);
    const [activeOrders, setActiveOrders] = useState([]);
    const [pendingOrders, setPendingOrders] = useState([]);
    const [safetyAlerts, setSafetyAlerts] = useState([]);
    const [showOrderWorkflow, setShowOrderWorkflow] = useState(false);

    // Two-step administration interface state
    const [showAdministrationSessionManager, setShowAdministrationSessionManager] = useState(false);
    const [selectedMedication, setSelectedMedication] = useState(null);

    // Approval state
    const [approvalLoading, setApprovalLoading] = useState(null);

    // Workflow details state
    const [showWorkflowDetailsModal, setShowWorkflowDetailsModal] = useState(false);
    const [selectedWorkflow, setSelectedWorkflow] = useState(null);
    const [workflowDetailsLoading, setWorkflowDetailsLoading] = useState(false);

    const [stats, setStats] = useState({
        totalMedications: 0,
        activePrescriptions: 0,
        pendingOrders: 0,
        safetyAlerts: 0,
        dueNow: 0,
        dueSoon: 0
    });

    // Load patient medication data on mount
    useEffect(() => {
        if (patient?.id) {
            loadPatientMedicationData();
        }
    }, [patient]);

    // Format ISO datetime string to GST (Asia/Dubai) HH:mm
    const formatTimeGST = (isoString) => {
        try {
            return new Intl.DateTimeFormat('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
                timeZone: 'Asia/Dubai'
            }).format(new Date(isoString));
        } catch (e) {
            return '';
        }
    };

    const loadPatientMedicationData = async () => {
        try {
            // First, try to load from smart medication dashboard for enhanced features
            const smartDashboardResult = await medpassService.getMedicationDashboard(patient.id);
            if (smartDashboardResult.success && smartDashboardResult.data?.medications) {
                console.log('Loaded smart dashboard data:', smartDashboardResult.data);
                // Use smart dashboard data with industry-standard status calculation
                const smartMedications = smartDashboardResult.data.medications.map(med => ({
                    ...med,
                    status: calculateStatus(
                        moment(med.scheduled_time),
                        med.is_completed,
                        med.administration_record
                    )
                }));
                setScheduledMedications(smartMedications);
            } else {
                // Fallback to legacy API
                const scheduledMedsResult = await medpassService.getPatientScheduledMedications(patient.id);
                if (scheduledMedsResult.success) {
                    console.log('Received scheduled medications from API:', scheduledMedsResult.data);
                    console.log('Sample medication structure:', scheduledMedsResult.data[0]);

                    // Debug administration times
                    scheduledMedsResult.data.forEach((med, index) => {
                        console.log(`Medication ${index + 1}: ${med.brand_name}`);
                        console.log(`  - Administration times:`, med.administration_times);
                        console.log(`  - Scheduled doses:`, med.scheduled_doses);
                        console.log(`  - Next due:`, med.next_due);
                    });

                    const scheduled = calculateMedicationSchedules(scheduledMedsResult.data || []);
                    console.log('Processed scheduled medications:', scheduled);
                    setScheduledMedications(scheduled);
                }
            }

            // Load current medications (for reference)
            const currentMedsResult = await medicationService.getPatientMedications(patient.id);
            if (currentMedsResult.success) {
                setCurrentMedications(currentMedsResult.data || []);
            }

            // Note: Medication history functionality removed for simplicity

            // Load active workflows
            const workflowsResult = await medpassService.getPatientActiveWorkflows(patient.id);
            if (workflowsResult.success) {
                const workflows = workflowsResult.data || [];
                setActiveWorkflows(workflows);

                // Filter workflows into active and pending orders
                const activeStatuses = ['approved', 'in_progress', 'completed'];
                const pendingStatuses = ['pending_review', 'pending'];

                // Base lists from summary
                let active = workflows.filter(wf => activeStatuses.includes(wf.status));
                const pending = workflows.filter(wf => pendingStatuses.includes(wf.status));

                // Enrich active workflows with full details (medications, scheduling_info, approver name)
                try {
                    const enriched = await Promise.all(
                        active.map(async (wf) => {
                            try {
                                const detail = await medpassService.getWorkflowById(wf.workflow_id);
                                if (detail.success && detail.data) {
                                    return { ...wf, ...detail.data };
                                }
                            } catch (e) {}
                            return wf;
                        })
                    );
                    active = enriched;
                } catch (e) {}

                setActiveOrders(active);
                setPendingOrders(pending);
            }

            // Load safety alerts
            const alertsResult = await medpassService.getPatientSafetyAlerts(patient.id);
            if (alertsResult.success) {
                setSafetyAlerts(alertsResult.data || []);
            }

            // Calculate statistics
            calculateMedicationStats();

        } catch (error) {
            console.error('Error loading patient medication data:', error);
            // Only show error message for non-404 errors (404 means endpoint not found yet)
            if (error.response?.status !== 404) {
                message.error('Failed to load medication information');
            }
        }
    };

    // Smart status calculation with industry-standard categories
    const calculateStatus = (nextDue, alreadyAdministered, administrationRecord = null) => {
        if (alreadyAdministered || administrationRecord) {
            // For administered medications, determine if early/on-time/late
            const scheduledTime = moment(nextDue);
            const administeredTime = administrationRecord ?
                moment(administrationRecord.administered_at) :
                moment(); // fallback to current time

            const diffMinutes = administeredTime.diff(scheduledTime, 'minutes');

            if (diffMinutes < -30) {
                return 'administered_early';
            } else if (diffMinutes >= -30 && diffMinutes <= 30) {
                return 'administered_on_time';
            } else {
                return 'administered_late';
            }
        }

        const now = moment();
        const timeDiff = nextDue.diff(now, 'minutes');

        // Industry-standard grace periods and status categories
        if (timeDiff < -120) { // More than 2 hours overdue - missed
            return 'missed';
        } else if (timeDiff < -30) { // 30 minutes to 2 hours overdue
            return 'overdue';
        } else if (timeDiff <= 0) { // Due now (within 30 minutes grace)
            return 'due_now';
        } else if (timeDiff <= 30) { // Due within next 30 minutes
            return 'due_soon';
        } else {
            return 'pending';
        }
    };

    // Calculate medication schedules based on frequency and dosing instructions
    const calculateMedicationSchedules = (medicationOrders) => {
        const now = moment();

        return medicationOrders.map(order => {
            console.log('Processing medication order:', {
                medication_id: order.medication_id,
                brand_name: order.brand_name,
                next_due: order.next_due,
                next_due_formatted: order.next_due_formatted,
                scheduled_doses: order.scheduled_doses,
                frequency: order.frequency,
                administration_times: order.administration_times
            });

            // Use the original custom administration times if available
            let scheduled_times = [];
            let nextDue;

            if (order.scheduled_doses && order.scheduled_doses.length > 0) {
                // Use the scheduled_doses from the API response
                scheduled_times = order.scheduled_doses.map(dose => moment(dose.scheduled_time));
                nextDue = order.next_due ? moment(order.next_due) : scheduled_times[0];
            } else if (order.scheduled_time) {
                // Fallback to single scheduled_time if scheduled_doses not available
                nextDue = moment(order.scheduled_time);
                scheduled_times = [nextDue];
            } else {
                // Fallback to local calculation if no timing data available
                scheduled_times = calculateDoseTimes(order.frequency, order.administration_times);
                nextDue = scheduled_times[0];
            }

            // Calculate status based on next due time
            const status = calculateStatus(nextDue, order.already_administered);

            return {
                ...order,
                next_due: nextDue,
                scheduled_times: scheduled_times,
                status: status,
                is_overdue: status === 'overdue',
                is_due_now: status === 'due_now',
                is_due_soon: status === 'due_soon'
            };
    // Format ISO datetime string to GST (Asia/Dubai) HH:mm
    const formatTimeGST = (isoString) => {
        try {
            return new Intl.DateTimeFormat('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
                timeZone: 'Asia/Dubai'
            }).format(new Date(isoString));
        } catch (e) {
            return '';
        }
    };

        });
    };

    // Format frequency for display
    const formatFrequency = (frequency) => {
        const frequencyMap = {
            'once_daily': 'Once daily',
            'twice_daily': 'Twice daily',
            'three_times_daily': 'Three times daily',
            'four_times_daily': 'Four times daily',
            'every_4_hours': 'Every 4 hours',
            'every_6_hours': 'Every 6 hours',
            'every_8_hours': 'Every 8 hours',
            'every_12_hours': 'Every 12 hours',
            'with_meals': 'With meals',
            'at_bedtime': 'At bedtime',
            'as_needed': 'As needed (PRN)'
        };

        return frequencyMap[frequency] || frequency;
    };

    // Calculate dose times based on frequency
    const calculateDoseTimes = (frequency, customTimes = null) => {
        const times = [];

        if (customTimes && customTimes.length > 0) {
            // Use custom administration times if provided
            return customTimes.map(time => moment(time, 'HH:mm'));
        }

        // Default scheduling based on frequency
        switch (frequency) {
            case 'once_daily':
                times.push(moment().hour(8).minute(0));
                break;
            case 'twice_daily':
                times.push(moment().hour(8).minute(0));
                times.push(moment().hour(20).minute(0));
                break;
            case 'three_times_daily':
                times.push(moment().hour(8).minute(0));
                times.push(moment().hour(14).minute(0));
                times.push(moment().hour(20).minute(0));
                break;
            case 'four_times_daily':
                times.push(moment().hour(8).minute(0));
                times.push(moment().hour(12).minute(0));
                times.push(moment().hour(16).minute(0));
                times.push(moment().hour(20).minute(0));
                break;
            case 'every_4_hours':
                for (let hour = 0; hour < 24; hour += 4) {
                    times.push(moment().hour(hour).minute(0));
                }
                break;
            case 'every_6_hours':
                for (let hour = 0; hour < 24; hour += 6) {
                    times.push(moment().hour(hour).minute(0));
                }
                break;
            case 'every_8_hours':
                times.push(moment().hour(8).minute(0));
                times.push(moment().hour(16).minute(0));
                times.push(moment().hour(0).minute(0));
                break;
            case 'with_meals':
                times.push(moment().hour(8).minute(0));   // Breakfast
                times.push(moment().hour(12).minute(0));  // Lunch
                times.push(moment().hour(18).minute(0));  // Dinner
                break;
            case 'at_bedtime':
                times.push(moment().hour(22).minute(0));
                break;
            default:
                times.push(moment().hour(8).minute(0));
        }

        return times;
    };

    // Find next due time
    const findNextDueTime = (scheduledTimes) => {
        const now = moment();
        const today = moment().startOf('day');

        // Get today's scheduled times
        const todayTimes = scheduledTimes.map(time =>
            today.clone().hour(time.hour()).minute(time.minute())
        );

        // Find next due time
        for (const time of todayTimes) {
            if (time.isAfter(now)) {
                return time;
            }
        }

        // If no more doses today, return first dose tomorrow
        return today.clone().add(1, 'day').hour(scheduledTimes[0].hour()).minute(scheduledTimes[0].minute());
    };

    // Determine due status
    const getDueStatus = (nextDue, currentTime) => {
        if (!nextDue) return 'scheduled';

        const diffMinutes = nextDue.diff(currentTime, 'minutes');

        if (diffMinutes < -30) return 'overdue';      // More than 30 minutes late
        if (diffMinutes <= 0) return 'due_now';      // Due now (within 30 minutes)
        if (diffMinutes <= 120) return 'due_soon';   // Due within 2 hours
        return 'scheduled';                           // Scheduled for later
    };

    const calculateMedicationStats = () => {
        // Enhanced statistics with industry-standard categories
        const dueNow = scheduledMedications.filter(med =>
            med.status === 'due_now' || med.status === 'overdue'
        ).length;
        const dueSoon = scheduledMedications.filter(med => med.status === 'due_soon').length;
        const missed = scheduledMedications.filter(med => med.status === 'missed').length;
        const administered = scheduledMedications.filter(med =>
            med.status?.startsWith('administered_')
        ).length;
        const canAdminister = scheduledMedications.filter(med =>
            med.can_administer && !med.is_completed
        ).length;

        setStats({
            totalMedications: scheduledMedications.length,
            activePrescriptions: scheduledMedications.filter(med => med.medication_status === 'active').length,
            pendingOrders: pendingOrders.length,
            safetyAlerts: safetyAlerts.filter(alert => alert.requires_action).length,
            dueNow: dueNow,
            dueSoon: dueSoon,
            missed: missed,
            administered: administered,
            canAdminister: canAdminister
        });
    };

    const handleOrderMedication = () => {
        setShowOrderWorkflow(true);
    };

    const handleWorkflowComplete = (completedWorkflow) => {
        setShowOrderWorkflow(false);
        loadPatientMedicationData();
        if (onMedicationOrdered) {
            onMedicationOrdered(completedWorkflow);
        }
        message.success('Medication order completed successfully');
    };

    const handleWorkflowCancel = () => {
        setShowOrderWorkflow(false);
    };

    // Medication administration handlers
    const handleAdministerMedication = (medication) => {
        console.log('Opening two-step administration for:', medication);
        setSelectedMedication(medication);
        setShowAdministrationSessionManager(true);
    };

    const handleAdministrationSessionClose = () => {
        setShowAdministrationSessionManager(false);
        setSelectedMedication(null);
    };

    const handleAdministrationSessionComplete = async () => {
        message.success('Medication administration completed successfully');
        handleAdministrationSessionClose();
        // Refresh medications data
        await loadPatientMedicationData();
    };

    const handleHoldMedication = (medication) => {
        Modal.confirm({
            title: 'Hold Medication',
            content: `Are you sure you want to hold ${medication.medication_name}?`,
            onOk: async () => {
                try {
                    const result = await medpassService.holdMedication(medication.workflow_id, medication.medication_id);
                    if (result.success) {
                        message.success('Medication placed on hold');
                        loadPatientMedicationData();
                    } else {
                        message.error('Failed to hold medication');
                    }
                } catch (error) {
                    message.error('Failed to hold medication');
                }
            }
        });
    };

    const handlePRNRequest = (medication) => {
        // For PRN medications, show a simplified administration interface
        Modal.confirm({
            title: 'PRN Medication Request',
            content: `Request ${medication.medication_name} for ${patient.full_name}?`,
            onOk: () => handleAdministerMedication(medication)
        });
    };

    // Handle workflow approval
    const handleApproveWorkflow = async (workflowId) => {
        try {
            setApprovalLoading(workflowId);

            const result = await medpassService.approvePatientContextWorkflow(workflowId);

            if (result.success) {
                message.success('Medication order approved successfully!');

                // Refresh all patient medication data to reflect the approval
                await loadPatientMedicationData();
            } else {
                message.error(result.message || 'Failed to approve medication order');
            }
        } catch (error) {
            console.error('Error approving workflow:', error);
            message.error('An error occurred while approving the medication order');
        } finally {
            setApprovalLoading(null);
        }
    };

    // Handle viewing workflow details
    const handleViewWorkflowDetails = async (workflow) => {
        try {
            setWorkflowDetailsLoading(true);
            setSelectedWorkflow(workflow);

            const result = await medpassService.getWorkflowById(workflow.workflow_id);

            if (result.success) {
                setSelectedWorkflow(result.data);
                setShowWorkflowDetailsModal(true);
            } else {
                message.error(result.message || 'Failed to load workflow details');
            }
        } catch (error) {
            console.error('Error loading workflow details:', error);
            message.error('An error occurred while loading workflow details');
        } finally {
            setWorkflowDetailsLoading(false);
        }
    };

    // Render patient safety overview
    const renderSafetyOverview = () => (
        <Card
            title={
                <Space>
                    <SafetyCertificateOutlined />
                    Patient Safety Information
                </Space>
            }
            size="small"
            style={{ marginBottom: 16 }}
        >
            <Row gutter={[16, 8]}>
                <Col xs={24} sm={12} md={6}>
                    <Alert
                        message="Known Allergies"
                        description={
                            patient?.allergies?.length > 0
                                ? patient.allergies.map((allergy, index) => (
                                    <Tag key={`allergy-${allergy.id || allergy.allergen_id || index}-${index}`} color="red" style={{ margin: '2px' }}>
                                        {typeof allergy === 'object' ? allergy.name || allergy.allergen : allergy}
                                    </Tag>
                                ))
                                : <Text type="secondary">No known allergies</Text>
                        }
                        type={patient?.allergies?.length > 0 ? "warning" : "info"}
                        showIcon
                        size="small"
                    />
                </Col>

                <Col xs={24} sm={12} md={6}>
                    <Alert
                        message="Medical Conditions"
                        description={
                            patient?.medical_conditions?.length > 0
                                ? patient.medical_conditions.slice(0, 3).map((condition, index) => (
                                    <Tag key={`condition-${condition.id || condition.condition_id || index}-${index}`} color="orange" style={{ margin: '2px' }}>
                                        {typeof condition === 'object' ? condition.name || condition.condition : condition}
                                    </Tag>
                                ))
                                : <Text type="secondary">No conditions recorded</Text>
                        }
                        type="info"
                        showIcon
                        size="small"
                    />
                </Col>

                <Col xs={24} sm={12} md={6}>
                    <Alert
                        message="Current Medications"
                        description={
                            <Text>{currentMedications.length} active medication(s)</Text>
                        }
                        type="info"
                        showIcon
                        size="small"
                    />
                </Col>

                <Col xs={24} sm={12} md={6}>
                    <Alert
                        message="Safety Alerts"
                        description={
                            <Text type={safetyAlerts.length > 0 ? "warning" : "secondary"}>
                                {safetyAlerts.length} active alert(s)
                            </Text>
                        }
                        type={safetyAlerts.length > 0 ? "warning" : "info"}
                        showIcon
                        size="small"
                    />
                </Col>
            </Row>
        </Card>
    );

    // Enhanced medication statistics with smart dashboard metrics
    const renderMedicationStats = () => (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6} lg={4} xl={3}>
                <Card size="small">
                    <Statistic
                        title="Due Now"
                        value={stats.dueNow}
                        prefix={<ExclamationCircleOutlined />}
                        valueStyle={{ color: stats.dueNow > 0 ? '#ff4d4f' : '#52c41a' }}
                    />
                </Card>
            </Col>
            <Col xs={12} sm={6} lg={4} xl={3}>
                <Card size="small">
                    <Statistic
                        title="Due Soon"
                        value={stats.dueSoon}
                        prefix={<ClockCircleOutlined />}
                        valueStyle={{ color: stats.dueSoon > 0 ? '#fa8c16' : '#52c41a' }}
                    />
                </Card>
            </Col>
            <Col xs={12} sm={6} lg={4} xl={3}>
                <Card size="small">
                    <Statistic
                        title="Can Administer"
                        value={stats.canAdminister || 0}
                        prefix={<MedicineBoxOutlined />}
                        valueStyle={{ color: '#52c41a' }}
                    />
                </Card>
            </Col>
            <Col xs={12} sm={6} lg={4} xl={3}>
                <Card size="small">
                    <Statistic
                        title="Completed Today"
                        value={stats.administered || 0}
                        prefix={<CheckCircleOutlined />}
                        valueStyle={{ color: '#1890ff' }}
                    />
                </Card>
            </Col>
            <Col xs={12} sm={6} lg={4} xl={3}>
                <Card size="small">
                    <Statistic
                        title="Missed"
                        value={stats.missed || 0}
                        prefix={<WarningOutlined />}
                        valueStyle={{ color: stats.missed > 0 ? '#ff4d4f' : '#52c41a' }}
                    />
                </Card>
            </Col>
            <Col xs={12} sm={6} lg={4} xl={3}>
                <Card size="small">
                    <Statistic
                        title="Pending Orders"
                        value={stats.pendingOrders}
                        prefix={<FileProtectOutlined />}
                        valueStyle={{ color: '#fa8c16' }}
                    />
                </Card>
            </Col>
            <Col xs={12} sm={6} lg={4} xl={3}>
                <Card size="small">
                    <Statistic
                        title="Safety Alerts"
                        value={stats.safetyAlerts}
                        prefix={<WarningOutlined />}
                        valueStyle={{ color: stats.safetyAlerts > 0 ? '#ff4d4f' : '#52c41a' }}
                    />
                </Card>
            </Col>
            <Col xs={12} sm={6} lg={4} xl={3}>
                <Card size="small">
                    <Statistic
                        title="Total Active"
                        value={stats.totalMedications}
                        prefix={<MedicineBoxOutlined />}
                        valueStyle={{ color: '#1890ff' }}
                    />
                </Card>
            </Col>
        </Row>
    );

    // Render medication schedule (MAR interface)
    const renderMedicationSchedule = () => {
        // Smart medication status configuration with industry-standard categories
        const getMedicationStatusConfig = (status) => {
            const configs = {
                'pending': {
                    color: 'default',
                    text: 'Pending',
                    bgColor: '#f5f5f5',
                    antdColor: '#d9d9d9'
                },
                'due_now': {
                    color: 'processing',
                    text: 'Due Now',
                    bgColor: '#e6f7ff',
                    antdColor: '#1890ff'
                },
                'due_soon': {
                    color: 'warning',
                    text: 'Due Soon',
                    bgColor: '#fff7e6',
                    antdColor: '#fa8c16'
                },
                'overdue': {
                    color: 'warning',
                    text: 'Overdue',
                    bgColor: '#fff2f0',
                    antdColor: '#ff7a45'
                },
                'missed': {
                    color: 'error',
                    text: 'Missed',
                    bgColor: '#fff2f0',
                    antdColor: '#ff4d4f'
                },
                'administered_on_time': {
                    color: 'success',
                    text: 'Administered',
                    bgColor: '#f6ffed',
                    antdColor: '#52c41a'
                },
                'administered_early': {
                    color: 'success',
                    text: 'Early',
                    bgColor: '#f6ffed',
                    antdColor: '#52c41a'
                },
                'administered_late': {
                    color: 'warning',
                    text: 'Late',
                    bgColor: '#fff7e6',
                    antdColor: '#fa8c16'
                },
                'cancelled': {
                    color: 'error',
                    text: 'Cancelled',
                    bgColor: '#fff2f0',
                    antdColor: '#ff4d4f'
                }
            };
            return configs[status] || {
                color: 'default',
                text: status || 'Unknown',
                bgColor: '#f5f5f5',
                antdColor: '#d9d9d9'
            };
        };

        const getStatusColor = (status) => {
            return getMedicationStatusConfig(status).antdColor;
        };

        const getStatusText = (status) => {
            return getMedicationStatusConfig(status).text.toUpperCase();
        };

        const columns = [
            {
                title: 'Status',
                key: 'status',
                width: 100,
                render: (_, record) => (
                    <Badge
                        color={getStatusColor(record.status)}
                        text={getStatusText(record.status)}
                    />
                ),
                sorter: (a, b) => {
                    const statusOrder = { 'overdue': 0, 'due_now': 1, 'due_soon': 2, 'scheduled': 3 };
                    return statusOrder[a.status] - statusOrder[b.status];
                },
                defaultSortOrder: 'ascend'
            },
            {
                title: 'Medication',
                key: 'medication',
                render: (_, record) => (
                    <div>
                        <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                            {record.medication_name || record.brand_name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>
                            {record.dose_amount} • {record.route} • {formatFrequency(record.frequency)}
                        </div>
                        <div style={{ fontSize: '11px', color: '#999' }}>
                            {record.physician_instructions && record.physician_instructions.length > 50
                                ? `${record.physician_instructions.substring(0, 50)}...`
                                : record.physician_instructions
                            }
                        </div>
                    </div>
                )
            },
            {
                title: 'Next Due',
                key: 'next_due',
                width: 100,
                render: (_, record) => {
                    // Convert UTC times to local time for display
                    let nextDueTime = null;
                    const status = record.status;

                    if (record.next_due) {
                        // Display in GST (Asia/Dubai)
                        nextDueTime = formatTimeGST(record.next_due);
                    } else if (record.next_due_formatted) {
                        // Use formatted time if available
                        nextDueTime = record.next_due_formatted;
                    } else if (record.scheduled_doses && record.scheduled_doses.length > 0) {
                        // Find the next due dose from scheduled doses
                        const now = moment();
                        for (const dose of record.scheduled_doses) {
                            const formatted = formatTimeGST(dose.scheduled_time);
                            // Maintain ordering vs now using moment if available
                            try {
                                const utcTime = moment.utc(dose.scheduled_time);
                                const dubai = utcTime.tz ? utcTime.tz('Asia/Dubai') : utcTime;
                                if (dubai.isAfter(moment())) {
                                    nextDueTime = formatted;
                                    break;
                                }
                            } catch (e) {
                                nextDueTime = formatted;
                                break;
                            }
                        }

                        // If no future time found, use the first scheduled time
                        if (!nextDueTime && record.scheduled_doses.length > 0) {
                            nextDueTime = formatTimeGST(record.scheduled_doses[0].scheduled_time);
                        }
                    }

                    if (nextDueTime) {
                        return (
                            <div>
                                <div style={{ fontWeight: 'bold', color: getStatusColor(status) }}>
                                    {nextDueTime}
                                </div>
                                <div style={{ fontSize: '10px', color: '#999' }}>
                                    {getStatusText(status)}
                                </div>
                            </div>
                        );
                    } else {
                        return (
                            <div style={{ color: '#999', fontStyle: 'italic' }}>
                                No schedule
                            </div>
                        );
                    }
                }
            },
            {
                title: 'Last Administered',
                key: 'last_administered',
                width: 120,
                render: (_, record) => {
                    if (record.last_administered_utc || record.last_administered) {
                        // Prefer backend-provided local projection if present
                        if (record.last_administered_local_time && record.last_administered_local_date) {
                            return (
                                <div>
                                    <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
                                        {record.last_administered_local_time}
                                    </div>
                                    <div style={{ fontSize: '10px', color: '#999' }}>
                                        {record.last_administered_local_date}
                                    </div>
                                </div>
                            );
                        }
                        // Fall back to converting UTC to local
                        const iso = record.last_administered_utc || record.last_administered;
                        const localTimeStr = formatTimeGST(iso);
                        const localDateStr = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai' }).format(new Date(iso));
                        return (
                            <div>
                                <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
                                    {localTimeStr}
                                </div>
                                <div style={{ fontSize: '10px', color: '#999' }}>
                                    {localDateStr}
                                </div>
                            </div>
                        );
                    } else if (record.last_administered_formatted) {
                        // Use formatted time if available
                        return (
                            <div>
                                <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
                                    {record.last_administered_formatted}
                                </div>
                                <div style={{ fontSize: '10px', color: '#999' }}>
                                    {record.last_administered_date}
                                </div>
                            </div>
                        );
                    } else {
                        return (
                            <div style={{ color: '#999', fontStyle: 'italic' }}>
                                Not given today
                            </div>
                        );
                    }
                }
            },
            {
                title: 'Schedule',
                key: 'schedule',
                width: 140,
                render: (_, record) => {
                    // Convert UTC times to local time for display
                    let scheduledTimes = [];

                    if (record.scheduled_doses && record.scheduled_doses.length > 0) {
                        // Convert UTC scheduled times to GST
                        scheduledTimes = record.scheduled_doses.map(dose => formatTimeGST(dose.scheduled_time));
                    } else if (record.scheduled_times && record.scheduled_times.length > 0) {
                        // Convert calculated scheduled times to local time
                        scheduledTimes = record.scheduled_times.map(time => time.format('HH:mm'));
                    } else if (record.administration_times && record.administration_times.length > 0) {
                        // Use original administration times (these should already be in local time)
                        scheduledTimes = record.administration_times;
                    }

                    return (
                        <div>
                            <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                                {record.frequency ? formatFrequency(record.frequency) : 'Unknown'}
                            </div>
                            {scheduledTimes.length > 0 && (
                                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                    Times: {scheduledTimes.join(', ')}
                                </div>
                            )}
                            {record.dose_amount && (
                                <div style={{ fontSize: '10px', color: '#999' }}>
                                    {record.dose_amount}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                title: 'Actions',
                key: 'actions',
                width: 200,
                render: (_, record) => (
                    <Space direction="vertical" size="small">
                        <Space>
                            <Button
                                type="primary"
                                size="small"
                                icon={<PlayCircleOutlined />}
                                onClick={() => handleAdministerMedication(record)}
                                disabled={record.status === 'on_hold'}
                            >
                                Administer
                            </Button>
                            {/* <Button
                                size="small"
                                icon={<PauseCircleOutlined />}
                                onClick={() => handleHoldMedication(record)}
                                disabled={record.status === 'on_hold'}
                            >
                                Hold
                            </Button> */}
                        </Space>
                        {record.administration_type === 'prn' && (
                            <Button
                                size="small"
                                type="dashed"
                                icon={<FormOutlined />}
                                onClick={() => handlePRNRequest(record)}
                                style={{ width: '100%' }}
                            >
                                PRN Request
                            </Button>
                        )}
                    </Space>
                )
            }
        ];

        return (
            <Table
                columns={columns}
                dataSource={scheduledMedications}
                rowKey={(record, index) => `${record.medication_id}-${index}`}
                size="small"
                pagination={false}
                rowClassName={(record) => {
                    if (record.status === 'overdue') return 'medication-overdue';
                    if (record.status === 'due_now') return 'medication-due-now';
                    if (record.status === 'due_soon') return 'medication-due-soon';
                    return '';
                }}
                locale={{
                    emptyText: (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <MedicineBoxOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                            <div style={{ marginTop: 16, color: '#999' }}>
                                No scheduled medications for today
                            </div>
                            <div style={{ marginTop: 8, fontSize: '14px', color: '#666' }}>
                                Medication orders from completed workflows will appear here
                            </div>
                        </div>
                    )
                }}
            />
        );
    };

    // Render active orders (approved/in_progress/completed)
    const renderActiveOrders = () => {
        if (activeOrders.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <CheckCircleOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                    <div style={{ marginTop: 16, color: '#999' }}>
                        No active medication orders
                    </div>
                </div>
            );
        }

        return (
            <List
                dataSource={activeOrders}
                renderItem={(workflow, index) => (
                    <List.Item
                        key={`active-workflow-${workflow.workflow_id}-${index}`}
                        actions={[
                            <Button
                                key={`btn-${workflow.workflow_id}-${index}`}
                                size="small"
                                onClick={() => handleViewWorkflowDetails(workflow)}
                                loading={workflowDetailsLoading && selectedWorkflow?.workflow_id === workflow.workflow_id}
                            >
                                View Details
                            </Button>
                        ]}
                    >
                        <List.Item.Meta
                            avatar={<CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} />}
                            title={`Workflow #${workflow.workflow_id}`}
                            description={
                                <div>
                                    <div style={{ marginBottom: 8 }}>
                                        Status: <Badge color="green" text={workflow.status} />
                                        {workflow.current_step_display ? <> | Step: {workflow.current_step_display}</> : null}
                                        {workflow.created_at ? <> | Created: {moment(workflow.created_at).format('DD/MM/YYYY hh:mm A')}</> : null}
                                        {workflow.approved_at && workflow.approved_by_name ? <> | Approved: {moment(workflow.approved_at).format('DD/MM/YYYY hh:mm A')} by {workflow.approved_by_name}</> : null}
                                    </div>

                                    {Array.isArray(workflow.medications) && workflow.medications.length > 0 && (
                                        <div style={{ background: '#fafafa', padding: 12, borderRadius: 6, border: '1px solid #eee' }}>
                                            <div style={{ fontWeight: 600, marginBottom: 8 }}>Medications</div>
                                            <Row gutter={[8, 8]}>
                                                {workflow.medications.map((med, i) => {
                                                    const scheduling = workflow.scheduling_info || {};
                                                    const startDate = med.start_date || scheduling.start_date;
                                                    const endDate = med.end_date || scheduling.end_date;
                                                    const adminTimes = med.administration_times || scheduling.administration_times || [];
                                                    const route = med.route || med.route_of_administration || scheduling.route;
                                                    const freq = med.frequency || scheduling.frequency;
                                                    const instr = med.physician_instructions || med.directions;
                                                    const indication = med.indication;
                                                    const monFreq = med.monitoring_frequency || workflow.monitoring_requirements?.monitoring_frequency;
                                                    const monDur = med.monitoring_duration || workflow.monitoring_requirements?.monitoring_duration;
                                                    const preVitals = med.pre_administration_vitals || [];
                                                    const postVitals = med.post_administration_vitals || [];

                                                    return (
                                                        <Col key={`med-${i}`} xs={24} md={12} lg={8}>
                                                            <Card size="small">
                                                                <div style={{ fontWeight: 600 }}>
                                                                    {med.brand_name || med.generic_name}
                                                                    {(med.strength || med.dosage_form) && (
                                                                        <Tag color="blue" style={{ marginLeft: 8 }}>
                                                                            {med.strength || ''} {med.dosage_form || ''}
                                                                        </Tag>
                                                                    )}
                                                                </div>
                                                                <div style={{ fontSize: 12, color: '#555', marginTop: 6 }}>
                                                                    <div>Dose: {med.dose_amount || med.dose || '—'}</div>
                                                                    <div>Route: {route || '—'}</div>
                                                                    <div>Frequency: {freq || '—'}</div>
                                                                    <div>Duration: {med.duration || (med.duration_days ? `${med.duration_days} days` : '—')}</div>
                                                                    {startDate && <div>Start: {startDate}</div>}
                                                                    {endDate && <div>End: {endDate}</div>}
                                                                    {Array.isArray(adminTimes) && adminTimes.length > 0 && (
                                                                        <div>Times: {adminTimes.join(', ')}</div>
                                                                    )}
                                                                    {med.administration_type && <div>Type: {med.administration_type.toUpperCase()}</div>}
                                                                    {instr && <div style={{ marginTop: 4 }}>Instructions: {instr}</div>}
                                                                    {indication && <div>Indication: {indication}</div>}
                                                                    {(preVitals.length > 0 || postVitals.length > 0) && (
                                                                        <div style={{ marginTop: 6 }}>
                                                                            Monitoring: {monFreq || '—'}{monDur ? ` for ${monDur} days` : ''}
                                                                            {preVitals.length > 0 && <div>Pre-vitals: {preVitals.join(', ')}</div>}
                                                                            {postVitals.length > 0 && <div>Post-vitals: {postVitals.join(', ')}</div>}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Card>
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        </div>
                                    )}
                                </div>
                            }
                        />
                    </List.Item>
                )}
            />
        );
    };

    // Render pending orders (pending_review/pending)
    const renderPendingOrders = () => {
        if (pendingOrders.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <ClockCircleOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                    <div style={{ marginTop: 16, color: '#999' }}>
                        No pending medication orders
                    </div>
                </div>
            );
        }

        return (
            <List
                dataSource={pendingOrders}
                renderItem={(workflow, index) => (
                    <List.Item
                        key={`pending-workflow-${workflow.workflow_id}-${index}`}
                        actions={[
                            workflow.status === 'pending_review' && (
                                <Button
                                    key={`approve-${workflow.workflow_id}-${index}`}
                                    type="primary"
                                    size="small"
                                    icon={<CheckCircleOutlined />}
                                    onClick={() => handleApproveWorkflow(workflow.workflow_id)}
                                    loading={approvalLoading === workflow.workflow_id}
                                >
                                    Approve Order
                                </Button>
                            ),
                            <Button
                                key={`btn-${workflow.workflow_id}-${index}`}
                                size="small"
                                onClick={() => handleViewWorkflowDetails(workflow)}
                                loading={workflowDetailsLoading && selectedWorkflow?.workflow_id === workflow.workflow_id}
                            >
                                View Details
                            </Button>
                        ].filter(Boolean)}
                    >
                        <List.Item.Meta
                            avatar={<ClockCircleOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />}
                            title={`Workflow #${workflow.workflow_id}`}
                            description={
                                <div>
                                    <div>Status: <Badge color="orange" text={workflow.status} /></div>
                                    <div>Step: {workflow.current_step_display}</div>
                                    <div>Created: {moment(workflow.created_at).format('DD/MM/YYYY hh:mm A')}</div>
                                </div>
                            }
                        />
                    </List.Item>
                )}
            />
        );
    };

    // Render safety alerts
    const renderSafetyAlerts = () => {
        if (safetyAlerts.length === 0) {
            return (
                <Alert
                    message="No Safety Alerts"
                    description="No active safety alerts for this patient"
                    type="success"
                    showIcon
                />
            );
        }

        return (
            <Space direction="vertical" style={{ width: '100%' }}>
                {safetyAlerts.filter(alert => alert).map((alert, index) => (
                    <Alert
                        key={`safety-alert-${alert.id || alert.alert_id || index}-${index}`}
                        message={alert.alert_type || 'Safety Alert'}
                        description={alert.message || 'No description available'}
                        type={alert.severity === 'critical' ? 'error' : 'warning'}
                        showIcon
                        action={
                            alert.requires_action && (
                                <Button size="small" type="primary">
                                    Review
                                </Button>
                            )
                        }
                    />
                ))}
            </Space>
        );
    };

    // Render two-step administration session manager
    const renderAdministrationSessionManager = () => {
        if (!selectedMedication || !showAdministrationSessionManager) return null;

        return (
            <AdministrationSessionManager
                medication={selectedMedication}
                patient={patient}
                visible={showAdministrationSessionManager}
                onClose={handleAdministrationSessionClose}
                onComplete={handleAdministrationSessionComplete}
            />
        );
    };

    // Main render
    return (
        <div className="patient-medication-tab">
            {/* Patient Context Header */}
            <Card
                size="small"
                title={
                    <Space>
                        <UserOutlined />
                        Medication Management: {patient?.full_name} (MRN: {patient?.mrn})
                    </Space>
                }
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleOrderMedication}
                        size="large"
                    >
                        Order New Medication
                    </Button>
                }
                style={{ marginBottom: 16 }}
            >
                {/* Safety Overview - Always visible */}
                {renderSafetyOverview()}

                {/* Medication Statistics */}
                {renderMedicationStats()}
            </Card>

            {/* Main Content Tabs */}
            <Card>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    type="card"
                    items={[
                        {
                            key: 'schedule',
                            label: (
                                <span>
                                    <ClockCircleOutlined />
                                    Medication Dashboard
                                </span>
                            ),
                            children: (
                                <SmartMedicationDashboard
                                    embeddedMode={true}
                                    selectedPatientId={patient?.id}
                                />
                            )
                        },
                        {
                            key: 'active_orders',
                            label: (
                                <span>
                                    <CheckCircleOutlined />
                                    Active Orders ({activeOrders.length})
                                </span>
                            ),
                            children: renderActiveOrders()
                        },
                        {
                            key: 'pending_orders',
                            label: (
                                <span>
                                    <ClockCircleOutlined />
                                    Pending Orders ({pendingOrders.length})
                                </span>
                            ),
                            children: renderPendingOrders()
                        },
                        {
                            key: 'history',
                            label: (
                                <span>
                                    <HistoryOutlined />
                                    Medication History
                                </span>
                            ),
                            children: (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <HistoryOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                                    <div style={{ marginTop: 16, color: '#999' }}>
                                        Medication history will be displayed here
                                    </div>
                                </div>
                            )
                        },
                        {
                            key: 'alerts',
                            label: (
                                <span>
                                    <WarningOutlined />
                                    Safety Alerts
                                    {safetyAlerts.length > 0 && <Badge count={safetyAlerts.length} size="small" style={{ marginLeft: 8 }} />}
                                </span>
                            ),
                            children: renderSafetyAlerts()
                        }
                    ]}
                />
            </Card>

            {/* Patient-Context MedPass Workflow Modal */}
            <Modal
                title={`Order Medication - ${patient?.full_name}`}
                open={showOrderWorkflow}
                onCancel={handleWorkflowCancel}
                footer={null}
                width="90%"
                style={{ maxWidth: '1200px' }}
                destroyOnHidden={true}
            >
                <PatientContextMedPassWorkflow
                    patient={patient}
                    onComplete={handleWorkflowComplete}
                    onCancel={handleWorkflowCancel}
                />
            </Modal>

            {/* Two-Step Administration Session Manager */}
            {renderAdministrationSessionManager()}

            {/* Workflow Details Modal */}
            <Modal
                title={
                    <Space>
                        <FormOutlined />
                        Workflow Details
                        {selectedWorkflow && (
                            <Tag color="blue">#{selectedWorkflow.workflow_id}</Tag>
                        )}
                    </Space>
                }
                open={showWorkflowDetailsModal}
                onCancel={() => {
                    setShowWorkflowDetailsModal(false);
                    setSelectedWorkflow(null);
                }}
                footer={[
                    <Button
                        key="close"
                        onClick={() => {
                            setShowWorkflowDetailsModal(false);
                            setSelectedWorkflow(null);
                        }}
                    >
                        Close
                    </Button>
                ]}
                width={800}
                destroyOnClose={true}
            >
                {selectedWorkflow ? (
                    <div>
                        {/* Workflow Status */}
                        <Card size="small" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Statistic
                                        title="Status"
                                        value={selectedWorkflow.status}
                                        valueStyle={{
                                            color: selectedWorkflow.status === 'approved' ? '#52c41a' : 
                                                   selectedWorkflow.status === 'pending_review' ? '#fa8c16' : '#1890ff'
                                        }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <Statistic
                                        title="Progress"
                                        value={selectedWorkflow.progress_percentage || 0}
                                        suffix="%"
                                        valueStyle={{ color: '#1890ff' }}
                                    />
                                </Col>
                            </Row>
                        </Card>

                        {/* Patient Information */}
                        {selectedWorkflow.patient_profile && (
                            <Card size="small" title="Patient Information" style={{ marginBottom: 16 }}>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Text strong>Name:</Text> {selectedWorkflow.patient_profile.full_name}
                                    </Col>
                                    <Col span={12}>
                                        <Text strong>MRN:</Text> {selectedWorkflow.patient_profile.mrn}
                                    </Col>
                                </Row>
                            </Card>
                        )}

                        {/* Medications */}
                        {selectedWorkflow.medications && selectedWorkflow.medications.length > 0 && (
                            <Card size="small" title="Prescribed Medications" style={{ marginBottom: 16 }}>
                                <List
                                    dataSource={selectedWorkflow.medications}
                                    renderItem={(medication, index) => (
                                        <List.Item key={index}>
                                            <List.Item.Meta
                                                title={medication.brand_name || medication.generic_name}
                                                description={
                                                    <div>
                                                        <div>Dose: {medication.dose}</div>
                                                        <div>Frequency: {medication.frequency}</div>
                                                        <div>Route: {medication.route}</div>
                                                    </div>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        )}

                        {/* Scheduling Information */}
                        {selectedWorkflow.scheduling_info && (
                            <Card size="small" title="Scheduling Information" style={{ marginBottom: 16 }}>
                                {selectedWorkflow.scheduling_info.medications_scheduling && 
                                 selectedWorkflow.scheduling_info.medications_scheduling.length > 0 ? (
                                    <div>
                                        {/* Individual Medication Schedules */}
                                        <div>
                                            <Text strong style={{ fontSize: '14px', marginBottom: 12, display: 'block' }}>
                                                Individual Medication Schedules:
                                            </Text>
                                            <List
                                                dataSource={selectedWorkflow.scheduling_info.medications_scheduling}
                                                renderItem={(medSchedule, index) => {
                                                    // Find the corresponding medication from the main medications array to get duration
                                                    // First try to match by medication_id, then by brand_name (case-insensitive)
                                                    const correspondingMedication = selectedWorkflow.medications?.find(med => {
                                                        // Check medication_id match first
                                                        if (medSchedule.medication_id && med.medication_id) {
                                                            return med.medication_id === medSchedule.medication_id;
                                                        }
                                                        // Then check brand_name match (case-insensitive)
                                                        if (medSchedule.medication_name && med.brand_name) {
                                                            return med.brand_name.toLowerCase() === medSchedule.medication_name.toLowerCase();
                                                        }
                                                        // Finally check generic_name match
                                                        if (medSchedule.medication_name && med.generic_name) {
                                                            return med.generic_name.toLowerCase() === medSchedule.medication_name.toLowerCase();
                                                        }
                                                        return false;
                                                    });
                                                    
                                                    // Debug logging to help identify the matching issue
                                                    console.log('Medication Schedule:', {
                                                        medication_id: medSchedule.medication_id,
                                                        medication_name: medSchedule.medication_name,
                                                        found_corresponding: correspondingMedication ? {
                                                            medication_id: correspondingMedication.medication_id,
                                                            brand_name: correspondingMedication.brand_name,
                                                            duration: correspondingMedication.duration
                                                        } : 'NOT FOUND'
                                                    });
                                                    
                                                    // Calculate end date based on start date and duration for this specific medication
                                                    let calculatedEndDate = medSchedule.end_date;
                                                    if (!calculatedEndDate && medSchedule.start_date && correspondingMedication?.duration) {
                                                        const startDate = new Date(medSchedule.start_date);
                                                        const duration = correspondingMedication.duration;
                                                        
                                                        // Parse duration (e.g., "30 days", "3 days")
                                                        const durationMatch = duration.match(/(\d+)\s*(day|days|week|weeks|month|months)/i);
                                                        if (durationMatch) {
                                                            const amount = parseInt(durationMatch[1]);
                                                            const unit = durationMatch[2].toLowerCase();
                                                            
                                                            if (unit.startsWith('day')) {
                                                                startDate.setDate(startDate.getDate() + amount);
                                                            } else if (unit.startsWith('week')) {
                                                                startDate.setDate(startDate.getDate() + (amount * 7));
                                                            } else if (unit.startsWith('month')) {
                                                                startDate.setMonth(startDate.getMonth() + amount);
                                                            }
                                                            
                                                            calculatedEndDate = startDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD
                                                        }
                                                    }
                                                    
                                                    return (
                                                        <List.Item key={index} style={{ padding: '12px 0' }}>
                                                            <div style={{ width: '100%' }}>
                                                                <div style={{ 
                                                                    backgroundColor: '#fafafa', 
                                                                    padding: '12px', 
                                                                    borderRadius: '6px',
                                                                    border: '1px solid #e8e8e8'
                                                                }}>
                                                                    <Row gutter={16}>
                                                                        <Col span={24} style={{ marginBottom: 8 }}>
                                                                            <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                                                                                {medSchedule.medication_name}
                                                                            </Text>
                                                                            <Tag color="blue" style={{ marginLeft: 8 }}>
                                                                                {medSchedule.strength} {medSchedule.dosage_form}
                                                                            </Tag>
                                                                            {correspondingMedication?.duration && (
                                                                                <Tag color="orange" style={{ marginLeft: 4 }}>
                                                                                    {correspondingMedication.duration}
                                                                                </Tag>
                                                                            )}
                                                                        </Col>
                                                                    </Row>
                                                                    <Row gutter={16}>
                                                                        <Col span={12}>
                                                                            <Text strong>Frequency:</Text> {medSchedule.frequency || 'Not specified'}
                                                                        </Col>
                                                                        <Col span={12}>
                                                                            <Text strong>Route:</Text> {medSchedule.route || 'Not specified'}
                                                                        </Col>
                                                                    </Row>
                                                                    <Row gutter={16} style={{ marginTop: 8 }}>
                                                                        <Col span={12}>
                                                                            <Text strong>Start Date:</Text> {medSchedule.start_date || 'Not specified'}
                                                                        </Col>
                                                                        <Col span={12}>
                                                                            <Text strong>End Date:</Text> {calculatedEndDate || 'Not specified'}
                                                                        </Col>
                                                                    </Row>
                                                                    {medSchedule.administration_times && medSchedule.administration_times.length > 0 && (
                                                                        <Row style={{ marginTop: 8 }}>
                                                                            <Col span={24}>
                                                                                <Text strong>Administration Times:</Text>
                                                                                <div style={{ marginTop: 4 }}>
                                                                                    {medSchedule.administration_times.map((time, timeIndex) => (
                                                                                        <Tag key={timeIndex} color="green" style={{ margin: '2px' }}>
                                                                                            {time}
                                                                                        </Tag>
                                                                                    ))}
                                                                                </div>
                                                                            </Col>
                                                                        </Row>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </List.Item>
                                                    );
                                                }}
                                            />
                                        </div>
                                        
                                        {selectedWorkflow.status === 'pending_review' && (
                                            <Row style={{ marginTop: 16 }}>
                                                <Col span={24}>
                                                    <Alert
                                                        message="Final scheduling will be confirmed after physician approval"
                                                        type="warning"
                                                        showIcon
                                                        size="small"
                                                    />
                                                </Col>
                                            </Row>
                                        )}
                                    </div>
                                ) : (
                                    // Fallback to old format if medications_scheduling is not available
                                    <>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Text strong>Frequency:</Text> {selectedWorkflow.scheduling_info.frequency || 'Not specified'}
                                            </Col>
                                            <Col span={12}>
                                                <Text strong>Administration Method:</Text> {selectedWorkflow.scheduling_info.administration_method || 'Not specified'}
                                            </Col>
                                        </Row>
                                        {selectedWorkflow.scheduling_info.administration_times && 
                                         selectedWorkflow.scheduling_info.administration_times.length > 0 && (
                                            <Row style={{ marginTop: 8 }}>
                                                <Col span={24}>
                                                    <Text strong>Administration Times:</Text> {selectedWorkflow.scheduling_info.administration_times.join(', ')}
                                                </Col>
                                            </Row>
                                        )}
                                        <Row style={{ marginTop: 12 }}>
                                            <Col span={24}>
                                                <Alert
                                                    message="Detailed medication scheduling information not available"
                                                    type="info"
                                                    showIcon
                                                    size="small"
                                                />
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </Card>
                        )}

                        {/* Physician Notes */}
                        {selectedWorkflow.physician_notes && selectedWorkflow.physician_notes.length > 0 && (
                            <Card size="small" title="Physician Notes">
                                <List
                                    dataSource={selectedWorkflow.physician_notes}
                                    renderItem={(note, index) => (
                                        <List.Item key={index}>
                                            <List.Item.Meta
                                                title={note.note_type}
                                                description={note.content}
                                            />
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        )}

                        {/* Metadata */}
                        <Card size="small" title="Workflow Information" style={{ marginTop: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Text strong>Created:</Text> {moment(selectedWorkflow.created_at).format('DD/MM/YYYY hh:mm A')}
                                </Col>
                                <Col span={12}>
                                    <Text strong>Last Updated:</Text> {moment(selectedWorkflow.updated_at).format('DD/MM/YYYY hh:mm A')}
                                </Col>
                            </Row>
                        </Card>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <FormOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                        <div style={{ marginTop: 16, color: '#999' }}>
                            Loading workflow details...
                        </div>
                    </div>
                )}
            </Modal>

            {/* Custom styles for medication status rows */}
            <style jsx>{`
                .medication-overdue {
                    background-color: #fff2f0 !important;
                    border-left: 4px solid #ff4d4f !important;
                }
                .medication-due-now {
                    background-color: #fff7e6 !important;
                    border-left: 4px solid #fa8c16 !important;
                }
                .medication-due-soon {
                    background-color: #f6ffed !important;
                    border-left: 4px solid #52c41a !important;
                }
            `}</style>
        </div>
    );
};

export default PatientMedicationTab;